import { type ChangeEvent, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Spinner } from '@/components/ui/Spinner';
import { useActiveOrgSlug } from '@/lib/activeOrg';
import { useMe } from '@/lib/auth';
import {
  type CategoryResolutionInput,
  useBulkResolveCategories,
} from '@/lib/categoryAliases';
import { cn } from '@/lib/cn';
import { type PartCategory, usePartCategories } from '@/lib/parts';
import {
  type ImportCommitResponse,
  type RowOutcome,
  type UploadResponse,
  type VendorEnrichmentSummary,
  type VendorPolicy,
  useCommitImport,
  useUploadSpreadsheet,
} from '@/lib/partsImport';

type Step = 'upload' | 'map' | 'review' | 'done';

export function ImportPartsPage() {
  const me = useMe();
  const orgSlug = useActiveOrgSlug(me.data?.organizations);

  const [step, setStep] = useState<Step>('upload');
  const [upload, setUpload] = useState<UploadResponse | null>(null);
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [skipDuplicates, setSkipDuplicates] = useState(true);
  // Vendor enrichment policy. Default off (`spreadsheet_wins`) — we
  // never silently make outbound API calls without an opt-in.
  const [vendorPolicy, setVendorPolicy] =
    useState<VendorPolicy>('spreadsheet_wins');
  const [preview, setPreview] = useState<ImportCommitResponse | null>(null);
  const [finalResult, setFinalResult] = useState<ImportCommitResponse | null>(null);

  const uploadMutation = useUploadSpreadsheet(orgSlug);
  const commitMutation = useCommitImport(orgSlug);

  const onFileSelected = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      const result = await uploadMutation.mutateAsync(file);
      setUpload(result);
      setMapping(result.auto_detected_mapping);
      setStep('map');
    } catch {
      /* surfaced below */
    }
  };

  const runPreview = async () => {
    if (!upload) return;
    try {
      const result = await commitMutation.mutateAsync({
        upload_id: upload.upload_id,
        column_mapping: mapping,
        skip_duplicates: skipDuplicates,
        dry_run: true,
        // Skip vendor enrichment on the dry-run preview — it'd waste
        // adapter calls + quota for a no-op. The real commit gets the
        // policy applied below.
        vendor_policy: 'spreadsheet_wins',
      });
      setPreview(result);
      setStep('review');
    } catch {
      /* surfaced below */
    }
  };

  const runCommit = async () => {
    if (!upload) return;
    try {
      const result = await commitMutation.mutateAsync({
        upload_id: upload.upload_id,
        column_mapping: mapping,
        skip_duplicates: skipDuplicates,
        dry_run: false,
        vendor_policy: vendorPolicy,
      });
      setFinalResult(result);
      setStep('done');
    } catch {
      /* surfaced below */
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <Link to="/parts" className="text-sm text-stone-500 dark:text-stone-400 hover:text-stone-700 dark:hover:text-stone-200">
          ← Parts
        </Link>
        <h1 className="mt-1 text-2xl font-semibold tracking-tight">
          Import parts from a spreadsheet
        </h1>
        <p className="mt-1 text-stone-600 dark:text-stone-400">
          Upload an .xlsx or .csv, map your columns to PartForge fields, then
          preview before committing.
        </p>
      </div>

      <Stepper step={step} />

      {step === 'upload' && (
        <UploadStep
          onSelect={onFileSelected}
          uploading={uploadMutation.isPending}
          error={uploadMutation.error as Error | null}
        />
      )}

      {step === 'map' && upload && (
        <MapStep
          upload={upload}
          mapping={mapping}
          onChange={setMapping}
          skipDuplicates={skipDuplicates}
          onSkipDuplicatesChange={setSkipDuplicates}
          vendorPolicy={vendorPolicy}
          onVendorPolicyChange={setVendorPolicy}
          onCancel={() => {
            setUpload(null);
            setMapping({});
            setStep('upload');
          }}
          onContinue={runPreview}
          previewing={commitMutation.isPending}
          error={commitMutation.error as Error | null}
        />
      )}

      {step === 'review' && upload && preview && (
        <ReviewStep
          upload={upload}
          preview={preview}
          onBack={() => setStep('map')}
          onCommit={runCommit}
          committing={commitMutation.isPending}
          error={commitMutation.error as Error | null}
        />
      )}

      {step === 'done' && finalResult && <DoneStep result={finalResult} orgSlug={orgSlug} />}
    </div>
  );
}

function Stepper({ step }: { step: Step }) {
  const steps: Array<{ key: Step; label: string }> = [
    { key: 'upload', label: '1. Upload' },
    { key: 'map', label: '2. Map columns' },
    { key: 'review', label: '3. Review' },
    { key: 'done', label: '4. Done' },
  ];
  const activeIdx = steps.findIndex((s) => s.key === step);
  return (
    <ol className="flex items-center gap-2 text-sm">
      {steps.map((s, idx) => (
        <li
          key={s.key}
          className={cn(
            'rounded-md px-3 py-1',
            idx === activeIdx
              ? 'bg-forge-100 dark:bg-forge-900 font-medium text-forge-800 dark:text-forge-200 dark:text-forge-300'
              : idx < activeIdx
                ? 'bg-stone-100 dark:bg-stone-800 text-stone-500 dark:text-stone-400'
                : 'text-stone-400 dark:text-stone-500',
          )}
        >
          {s.label}
        </li>
      ))}
    </ol>
  );
}

function UploadStep({
  onSelect,
  uploading,
  error,
}: {
  onSelect: (event: ChangeEvent<HTMLInputElement>) => void;
  uploading: boolean;
  error: Error | null;
}) {
  return (
    <section className="rounded-lg border dashed border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-900 p-8 text-center">
      <p className="text-sm text-stone-600 dark:text-stone-400">
        Drop or pick an .xlsx or .csv file. The first row should be your headers.
      </p>
      <label className="mt-4 inline-block cursor-pointer rounded-md bg-forge-600 px-4 py-2 text-sm font-medium text-white hover:bg-forge-700">
        {uploading ? 'Uploading…' : 'Choose file'}
        <input
          type="file"
          accept=".xlsx,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv"
          className="hidden"
          onChange={onSelect}
          disabled={uploading}
        />
      </label>
      <p className="mt-3 text-xs text-stone-500 dark:text-stone-400">
        Files are kept in memory for an hour. Max 10 MB.
      </p>
      {error && (
        <p className="mt-4 text-sm text-red-600">{error.message}</p>
      )}
    </section>
  );
}

function MapStep({
  upload,
  mapping,
  onChange,
  skipDuplicates,
  onSkipDuplicatesChange,
  vendorPolicy,
  onVendorPolicyChange,
  onCancel,
  onContinue,
  previewing,
  error,
}: {
  upload: UploadResponse;
  mapping: Record<string, string>;
  onChange: (next: Record<string, string>) => void;
  skipDuplicates: boolean;
  onSkipDuplicatesChange: (value: boolean) => void;
  vendorPolicy: VendorPolicy;
  onVendorPolicyChange: (value: VendorPolicy) => void;
  onCancel: () => void;
  onContinue: () => void;
  previewing: boolean;
  error: Error | null;
}) {
  // Reverse the mapping for the column-driven view: each spreadsheet
  // column has at most one PartForge field assigned. The wire format
  // stays `{field: column}` so the API contract doesn't change.
  const fieldByColumn = new Map<string, string>();
  for (const [field, column] of Object.entries(mapping)) {
    if (column) fieldByColumn.set(column, field);
  }

  const handleColumnAssign = (column: string, field: string) => {
    const next = { ...mapping };
    // 1. Drop any existing mapping that pointed at this column. Lets a
    // single-select dropdown re-assign cleanly.
    for (const [f, c] of Object.entries(next)) {
      if (c === column) delete next[f];
    }
    if (field === '') {
      onChange(next);
      return;
    }
    // 2. Drop any prior column for this field — fields only live in one
    // column at a time. The user re-assigning a field naturally moves
    // it from the old column to the new one.
    if (next[field]) delete next[field];
    next[field] = column;
    onChange(next);
  };

  const mappedColumnCount = fieldByColumn.size;
  const unmappedColumnCount = upload.columns.filter(
    (c) => c && !fieldByColumn.has(c),
  ).length;

  return (
    <section className="space-y-4">
      <div className="rounded-lg border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900">
        <div className="border-b border-stone-200 dark:border-stone-800 px-4 py-3 text-sm">
          <p>
            <span className="font-medium">{upload.filename}</span> —{' '}
            {upload.total_rows} rows, {upload.columns.length} columns.{' '}
            <span className="text-stone-500 dark:text-stone-400">
              {mappedColumnCount} mapped · {unmappedColumnCount} unmapped
            </span>
          </p>
          <p className="mt-1 text-xs text-stone-500 dark:text-stone-400">
            One row per spreadsheet column. Pick the matching PartForge
            field, or leave columns you don't care about set to "skip".
            Auto-detected guesses are pre-filled.
          </p>
        </div>
        <table className="w-full text-left text-sm">
          <thead className="bg-stone-50 dark:bg-stone-900 text-xs uppercase text-stone-500 dark:text-stone-400">
            <tr>
              <th className="px-3 py-2 w-1/2">Spreadsheet column</th>
              <th className="px-3 py-2">PartForge field</th>
            </tr>
          </thead>
          <tbody>
            {upload.columns.filter(Boolean).map((column) => {
              const assignedField = fieldByColumn.get(column) ?? '';
              const isMapped = assignedField !== '';
              return (
                <tr
                  key={column}
                  className="border-t border-stone-100 dark:border-stone-800"
                >
                  <td className="px-3 py-2 align-top">
                    <div className="flex items-center gap-2">
                      <span
                        aria-hidden
                        className={
                          isMapped
                            ? 'text-emerald-600 dark:text-emerald-400'
                            : 'text-stone-300 dark:text-stone-700'
                        }
                        title={isMapped ? 'mapped' : 'unmapped — will be ignored'}
                      >
                        {isMapped ? '✓' : '○'}
                      </span>
                      <span className="font-mono text-stone-700 dark:text-stone-300">
                        {column}
                      </span>
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <select
                      value={assignedField}
                      onChange={(e) =>
                        handleColumnAssign(column, e.target.value)
                      }
                      className="block w-full rounded-md border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-900 px-2 py-1.5 text-sm"
                    >
                      <option value="">— skip this column —</option>
                      {upload.supported_fields.map((descriptor) => {
                        const otherColumn = mapping[descriptor.field];
                        const claimedElsewhere =
                          otherColumn && otherColumn !== column;
                        return (
                          <option key={descriptor.field} value={descriptor.field}>
                            {descriptor.label}
                            {claimedElsewhere ? `  (currently from "${otherColumn}")` : ''}
                          </option>
                        );
                      })}
                    </select>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="rounded-lg border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900 p-4">
        <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-stone-500 dark:text-stone-400">
          Sample rows
        </h3>
        <div className="overflow-x-auto">
          <table className="text-xs">
            <thead>
              <tr className="text-stone-500 dark:text-stone-400">
                {upload.columns.map((c) => (
                  <th key={c} className="border-b border-stone-200 dark:border-stone-800 px-2 py-1 text-left font-medium">
                    {c}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {upload.sample_rows.map((row, idx) => (
                <tr key={idx}>
                  {upload.columns.map((c) => (
                    <td key={c} className="border-b border-stone-100 dark:border-stone-800 px-2 py-1 align-top text-stone-700 dark:text-stone-300">
                      {row[c] !== undefined && row[c] !== null ? String(row[c]) : ''}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={skipDuplicates}
          onChange={(e) => onSkipDuplicatesChange(e.target.checked)}
          className="h-4 w-4 rounded border border-stone-300 dark:border-stone-700 text-forge-600 focus:ring-forge-500"
        />
        Skip rows whose mfg PN already exists in this org
      </label>

      <fieldset className="space-y-2 rounded-lg border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900 p-3">
        <legend className="px-1 text-xs font-semibold uppercase tracking-wide text-stone-500 dark:text-stone-400">
          Pull live data from distributors
        </legend>
        <p className="text-xs text-stone-500 dark:text-stone-400">
          Rows with a recognized distributor PN (LCSC for now) can be looked
          up against the vendor and merged into the imported part. Failed
          lookups leave the spreadsheet row untouched.
        </p>
        <div className="space-y-1.5">
          <VendorPolicyRadio
            current={vendorPolicy}
            value="spreadsheet_wins"
            label="Off — spreadsheet wins"
            hint="Don't call any vendor APIs. Use only what's in the spreadsheet."
            onChange={onVendorPolicyChange}
          />
          <VendorPolicyRadio
            current={vendorPolicy}
            value="vendor_fills_blanks"
            label="Vendor fills blanks (recommended)"
            hint="Your typed values win where present; vendor data fills any field you left blank. Lossless."
            onChange={onVendorPolicyChange}
          />
          <VendorPolicyRadio
            current={vendorPolicy}
            value="vendor_wins"
            label="Vendor wins"
            hint="Vendor's value overrides yours where the vendor has data. Pick this when you trust LCSC more than your sheet."
            onChange={onVendorPolicyChange}
          />
        </div>
      </fieldset>

      {error && <p className="text-sm text-red-600">{error.message}</p>}

      <div className="flex justify-between">
        <Button variant="ghost" onClick={onCancel}>
          ← Pick a different file
        </Button>
        <Button onClick={onContinue} disabled={previewing}>
          {previewing ? (
            <>
              <Spinner className="mr-2 h-4 w-4" />
              Building preview…
            </>
          ) : (
            'Preview import →'
          )}
        </Button>
      </div>
    </section>
  );
}

function ReviewStep({
  upload,
  preview,
  onBack,
  onCommit,
  committing,
  error,
}: {
  upload: UploadResponse;
  preview: ImportCommitResponse;
  onBack: () => void;
  onCommit: () => void;
  committing: boolean;
  error: Error | null;
}) {
  return (
    <section className="space-y-4">
      <div className="rounded-lg border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900 p-4">
        <h2 className="text-lg font-semibold">Preview</h2>
        <p className="mt-1 text-sm text-stone-600 dark:text-stone-400">
          Dry run on <span className="font-medium">{upload.filename}</span>.
          Nothing has been written yet.
        </p>
        <dl className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Stat label="Will create" value={preview.created} tone="green" />
          <Stat label="Skipped (duplicate)" value={preview.skipped_duplicate} />
          <Stat label="Skipped (empty)" value={preview.skipped_empty} />
          <Stat label="Errored" value={preview.errored} tone="red" />
        </dl>
      </div>

      <OutcomesTable outcomes={preview.outcomes} />

      {error && <p className="text-sm text-red-600">{error.message}</p>}

      {committing && (
        <div className="flex items-start gap-3 rounded-md border border-stone-200 dark:border-stone-800 bg-stone-50 dark:bg-stone-900 p-3">
          <Spinner className="mt-0.5 h-5 w-5 text-forge-600 dark:text-forge-400" />
          <div className="flex-1 text-sm">
            <p className="font-medium">
              Importing {preview.created} parts…
            </p>
            <p className="mt-0.5 text-xs text-stone-600 dark:text-stone-400">
              Vendor enrichment can take a few seconds per row when
              enabled — the page is working, please don't navigate
              away.
            </p>
          </div>
        </div>
      )}

      <div className="flex justify-between">
        <Button variant="ghost" onClick={onBack} disabled={committing}>
          ← Back to mapping
        </Button>
        <Button onClick={onCommit} disabled={committing || preview.created === 0}>
          {committing ? (
            <>
              <Spinner className="mr-2 h-4 w-4" />
              Importing…
            </>
          ) : (
            `Commit ${preview.created} parts`
          )}
        </Button>
      </div>
    </section>
  );
}

function DoneStep({
  result,
  orgSlug,
}: {
  result: ImportCommitResponse;
  orgSlug: string | undefined;
}) {
  const navigate = useNavigate();
  return (
    <section className="space-y-4">
      <div className="rounded-lg border border-green-200 bg-green-50 p-4 text-sm text-green-900">
        <p className="font-medium">
          Imported {result.created} part{result.created === 1 ? '' : 's'}.
        </p>
        {result.skipped_duplicate > 0 && (
          <p className="mt-1">
            Skipped {result.skipped_duplicate} duplicate{result.skipped_duplicate === 1 ? '' : 's'}.
          </p>
        )}
        {result.errored > 0 && (
          <p className="mt-1 text-red-700">
            {result.errored} row{result.errored === 1 ? '' : 's'} errored — see details below.
          </p>
        )}
      </div>

      {result.vendor_enrichment && (
        <VendorEnrichmentBox summary={result.vendor_enrichment} />
      )}

      {result.unmapped_categories.length > 0 && (
        <UnmappedCategoryWizard
          terms={result.unmapped_categories}
          orgSlug={orgSlug}
        />
      )}

      <OutcomesTable outcomes={result.outcomes} />

      <div className="flex gap-2">
        <Button onClick={() => navigate('/parts')}>Go to parts</Button>
        {orgSlug && (
          <Button variant="secondary" onClick={() => navigate('/parts/import')}>
            Import another file
          </Button>
        )}
      </div>
    </section>
  );
}

function VendorPolicyRadio({
  current,
  value,
  label,
  hint,
  onChange,
}: {
  current: VendorPolicy;
  value: VendorPolicy;
  label: string;
  hint: string;
  onChange: (value: VendorPolicy) => void;
}) {
  return (
    <label className="flex cursor-pointer items-start gap-2 rounded p-1 text-sm hover:bg-stone-50 dark:hover:bg-stone-800/50">
      <input
        type="radio"
        name="vendor-policy"
        value={value}
        checked={current === value}
        onChange={() => onChange(value)}
        className="mt-0.5 h-4 w-4 border-stone-300 dark:border-stone-700 text-forge-600 focus:ring-forge-500"
      />
      <div className="flex-1">
        <div className="font-medium">{label}</div>
        <div className="text-xs text-stone-500 dark:text-stone-400">{hint}</div>
      </div>
    </label>
  );
}

function VendorEnrichmentBox({ summary }: { summary: VendorEnrichmentSummary }) {
  // The not-found / failure counters point to specific kinds of friction
  // a user might want to investigate; surface them only when nonzero so
  // the success case stays uncluttered.
  const hasIssues =
    summary.not_found > 0
    || summary.transient_failures > 0
    || summary.rate_limited > 0
    || summary.skipped_credentials > 0
    || summary.no_adapter > 0;
  return (
    <div className="rounded-lg border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900 p-4">
      <h3 className="text-sm font-semibold uppercase tracking-wide text-stone-500 dark:text-stone-400">
        Vendor enrichment
      </h3>
      <p className="mt-1 text-sm">
        Enriched <span className="font-medium">{summary.enriched}</span>{' '}
        {summary.enriched === 1 ? 'row' : 'rows'} from distributor data.
      </p>
      {hasIssues && (
        <ul className="mt-2 space-y-0.5 text-xs text-stone-600 dark:text-stone-400">
          {summary.not_found > 0 && (
            <li>{summary.not_found} not found at the upstream</li>
          )}
          {summary.transient_failures > 0 && (
            <li>
              {summary.transient_failures} transient failure
              {summary.transient_failures === 1 ? '' : 's'} (network or upstream
              hiccup)
            </li>
          )}
          {summary.rate_limited > 0 && (
            <li>
              {summary.rate_limited} hit rate-limit — try the rest later
            </li>
          )}
          {summary.skipped_credentials > 0 && (
            <li>
              {summary.skipped_credentials} skipped because the distributor
              isn't enabled in this org's settings
            </li>
          )}
          {summary.no_adapter > 0 && (
            <li>
              {summary.no_adapter} skipped — no PartForge adapter for that
              distributor yet
            </li>
          )}
        </ul>
      )}
      {summary.errors.length > 0 && (
        <details className="mt-2 text-xs text-stone-600 dark:text-stone-400">
          <summary className="cursor-pointer">
            Show {summary.errors.length} error message
            {summary.errors.length === 1 ? '' : 's'}
          </summary>
          <ul className="mt-1 list-disc space-y-0.5 pl-5">
            {summary.errors.map((e, i) => (
              <li key={i} className="font-mono">
                {e}
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone?: 'green' | 'red';
}) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-stone-500 dark:text-stone-400">{label}</dt>
      <dd
        className={cn(
          'text-2xl font-semibold',
          tone === 'green' && value > 0 && 'text-green-700',
          tone === 'red' && value > 0 && 'text-red-700',
        )}
      >
        {value}
      </dd>
    </div>
  );
}

function OutcomesTable({ outcomes }: { outcomes: RowOutcome[] }) {
  if (outcomes.length === 0) return null;
  return (
    <div className="overflow-hidden rounded-lg border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900">
      <div className="max-h-96 overflow-y-auto">
        <table className="w-full text-left text-sm">
          <thead className="sticky top-0 bg-stone-50 dark:bg-stone-900 text-xs uppercase text-stone-500 dark:text-stone-400">
            <tr>
              <th className="px-3 py-2">Row</th>
              <th className="px-3 py-2">Action</th>
              <th className="px-3 py-2">Part #</th>
              <th className="px-3 py-2">Mfg PN</th>
              <th className="px-3 py-2">Note</th>
            </tr>
          </thead>
          <tbody>
            {outcomes.map((o) => (
              <tr key={o.row_index} className="border-t border-stone-100 dark:border-stone-800">
                <td className="px-3 py-1.5 font-mono text-xs text-stone-500 dark:text-stone-400">
                  {o.row_index}
                </td>
                <td className="px-3 py-1.5">
                  <ActionBadge action={o.action} />
                </td>
                <td className="px-3 py-1.5 font-mono text-xs">{o.part_number ?? '—'}</td>
                <td className="px-3 py-1.5 font-mono text-xs">{o.mfg_pn ?? '—'}</td>
                <td className="px-3 py-1.5 text-xs text-stone-600 dark:text-stone-400">{o.message ?? ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/**
 * Post-import "teach us your terms" wizard. Lists every unmapped
 * category term the importer flagged and lets the user, per term:
 *
 * - Pick an existing PartForge category from a flat-then-grouped
 *   dropdown.
 * - Type a brand-new category name (creates it inline, makes the
 *   alias point at the new row).
 * - Skip — leave parts uncategorized for that term and don't store
 *   an alias.
 *
 * Submits all rows in one batch so the user gets atomic apply. After
 * a successful submit the panel collapses to a confirmation summary
 * so the user can see what was applied.
 */
function UnmappedCategoryWizard({
  terms,
  orgSlug,
}: {
  terms: string[];
  orgSlug: string | undefined;
}) {
  const categories = usePartCategories(orgSlug);
  const resolve = useBulkResolveCategories(orgSlug);

  type RowState = {
    term: string;
    action: 'map' | 'skip';
    pickedCategoryId: string;
    newCategoryName: string;
    newCategoryParentId: string;
  };

  const [rows, setRows] = useState<RowState[]>(() =>
    terms.map((t) => ({
      term: t,
      action: 'map',
      pickedCategoryId: '',
      newCategoryName: '',
      newCategoryParentId: '',
    })),
  );

  const updateRow = (idx: number, patch: Partial<RowState>) => {
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  };

  const onApply = () => {
    const payload: CategoryResolutionInput[] = rows.map((r) => {
      if (r.action === 'skip') {
        return { term: r.term, action: 'skip' };
      }
      if (r.newCategoryName.trim()) {
        return {
          term: r.term,
          action: 'map',
          new_category_name: r.newCategoryName.trim(),
          new_category_parent_id: r.newCategoryParentId
            ? Number(r.newCategoryParentId)
            : null,
        };
      }
      return {
        term: r.term,
        action: 'map',
        category_id: r.pickedCategoryId ? Number(r.pickedCategoryId) : null,
      };
    });
    resolve.mutate(payload);
  };

  const allValid = rows.every((r) => {
    if (r.action === 'skip') return true;
    return Boolean(r.pickedCategoryId || r.newCategoryName.trim());
  });

  if (resolve.isSuccess && resolve.data) {
    return (
      <div className="rounded-lg border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/20 p-4 text-sm">
        <p className="font-medium text-emerald-800 dark:text-emerald-300">
          Resolved {resolve.data.outcomes.length} term
          {resolve.data.outcomes.length === 1 ? '' : 's'}.
        </p>
        <ul className="mt-2 space-y-1 text-emerald-900 dark:text-emerald-200">
          {resolve.data.outcomes.map((o) => (
            <li key={o.term} className="text-xs">
              <span className="font-mono">{o.term}</span>
              {' → '}
              {o.action === 'skipped'
                ? 'skipped'
                : o.action === 'created_and_mapped'
                  ? `new category${o.parts_updated ? `, ${o.parts_updated} part${o.parts_updated === 1 ? '' : 's'} updated` : ''}`
                  : `mapped${o.parts_updated ? `, ${o.parts_updated} part${o.parts_updated === 1 ? '' : 's'} updated` : ''}`}
            </li>
          ))}
        </ul>
        <p className="mt-2 text-xs text-emerald-700 dark:text-emerald-400">
          Future imports of these terms will auto-resolve without prompting.
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-4 text-sm">
      <p className="font-medium text-amber-900 dark:text-amber-200">
        Unmapped category terms — teach PartForge what they mean
      </p>
      <p className="mt-1 text-xs text-amber-800 dark:text-amber-300">
        These category names weren't recognized. Pick a PartForge category
        for each, create a new one inline, or skip. Your choices are
        remembered for future imports of the same term.
      </p>

      <ul className="mt-3 space-y-2">
        {rows.map((row, idx) => (
          <li
            key={row.term}
            className="rounded border border-amber-200 dark:border-amber-800 bg-white dark:bg-stone-900 p-3"
          >
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded bg-amber-100 dark:bg-amber-900/30 px-2 py-0.5 font-mono text-xs text-amber-900 dark:text-amber-200">
                {row.term}
              </span>
              <span className="text-xs text-stone-500 dark:text-stone-400">
                →
              </span>

              <label className="flex items-center gap-1 text-xs">
                <input
                  type="radio"
                  checked={row.action === 'map'}
                  onChange={() => updateRow(idx, { action: 'map' })}
                />
                map to category
              </label>
              <label className="flex items-center gap-1 text-xs">
                <input
                  type="radio"
                  checked={row.action === 'skip'}
                  onChange={() => updateRow(idx, { action: 'skip' })}
                />
                skip
              </label>
            </div>

            {row.action === 'map' && (
              <div className="mt-2 grid gap-2 md:grid-cols-2">
                <label className="block">
                  <span className="mb-1 block text-xs text-stone-500 dark:text-stone-400">
                    Existing category
                  </span>
                  <select
                    value={row.pickedCategoryId}
                    onChange={(e) =>
                      updateRow(idx, {
                        pickedCategoryId: e.target.value,
                        // Picking existing nullifies the new-name input
                        newCategoryName: '',
                      })
                    }
                    disabled={Boolean(row.newCategoryName.trim())}
                    className="block w-full rounded-md border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-900 px-2 py-1.5 text-sm"
                  >
                    <option value="">— pick existing —</option>
                    {renderCategoryOptions(categories.data ?? [])}
                  </select>
                </label>
                <div>
                  <label className="block">
                    <span className="mb-1 block text-xs text-stone-500 dark:text-stone-400">
                      …or create new
                    </span>
                    <Input
                      value={row.newCategoryName}
                      onChange={(e) =>
                        updateRow(idx, {
                          newCategoryName: e.target.value,
                          // Typing a new name clears the picked-existing dropdown
                          pickedCategoryId: e.target.value
                            ? ''
                            : row.pickedCategoryId,
                        })
                      }
                      placeholder="e.g. Transducer"
                    />
                  </label>
                  {row.newCategoryName.trim() && (
                    <label className="mt-1 block">
                      <span className="mb-1 block text-xs text-stone-500 dark:text-stone-400">
                        Parent (optional)
                      </span>
                      <select
                        value={row.newCategoryParentId}
                        onChange={(e) =>
                          updateRow(idx, { newCategoryParentId: e.target.value })
                        }
                        className="block w-full rounded-md border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-900 px-2 py-1 text-xs"
                      >
                        <option value="">— top-level —</option>
                        {(categories.data ?? [])
                          .filter((c) => c.parent_id === null)
                          .map((c) => (
                            <option key={c.id} value={c.id}>
                              {c.name}
                            </option>
                          ))}
                      </select>
                    </label>
                  )}
                </div>
              </div>
            )}
          </li>
        ))}
      </ul>

      {resolve.isError && (
        <p className="mt-2 text-sm text-red-600">
          {(resolve.error as Error).message}
        </p>
      )}

      <div className="mt-3 flex justify-end">
        <Button
          type="button"
          onClick={onApply}
          disabled={!allValid || resolve.isPending}
        >
          {resolve.isPending ? 'Applying…' : 'Apply resolutions'}
        </Button>
      </div>
    </div>
  );
}

function renderCategoryOptions(categories: PartCategory[]) {
  // Two-level: top-level + nested children. Mirrors the optgroup shape
  // the rest of the app uses.
  const tops = categories.filter((c) => c.parent_id === null);
  const childrenByParent = new Map<number, PartCategory[]>();
  for (const c of categories) {
    if (c.parent_id != null) {
      const list = childrenByParent.get(c.parent_id) ?? [];
      list.push(c);
      childrenByParent.set(c.parent_id, list);
    }
  }
  return tops.map((top) => {
    const kids = childrenByParent.get(top.id) ?? [];
    if (kids.length === 0) {
      return (
        <option key={top.id} value={top.id}>
          {top.name}
        </option>
      );
    }
    return (
      <optgroup key={top.id} label={top.name}>
        <option value={top.id}>{top.name}</option>
        {kids.map((c) => (
          <option key={c.id} value={c.id}>
            {c.name}
          </option>
        ))}
      </optgroup>
    );
  });
}

function ActionBadge({ action }: { action: RowOutcome['action'] }) {
  const styles: Record<RowOutcome['action'], string> = {
    create: 'bg-green-100 text-green-800',
    'skip-duplicate': 'bg-amber-100 text-amber-800',
    'skip-empty': 'bg-stone-100 dark:bg-stone-800 text-stone-600 dark:text-stone-400',
    error: 'bg-red-100 text-red-800',
  };
  const labels: Record<RowOutcome['action'], string> = {
    create: 'create',
    'skip-duplicate': 'duplicate',
    'skip-empty': 'empty',
    error: 'error',
  };
  return (
    <span
      className={cn(
        'inline-flex rounded px-2 py-0.5 text-xs font-medium',
        styles[action],
      )}
    >
      {labels[action]}
    </span>
  );
}
